import React from 'react'
import {
  SelectFromObject,
  SingleDatePickerInput,
  TextAreaInput,
  TextInput,
  ToggleSwitchInput,
} from '../../../global/components/forms/index.js'
import ISReactDraftEditor from '../../../global/components/forms/ISReactDraftEditor.jsx'
import { DateTime } from 'luxon'
import RecipientInput from '../../quickTask/practice/components/RecipientInput.jsx'

export const RequestFilesLinkSettings = ({
  authType,
  authTypes,
  isAuthTypeRestricted,
  secretQuestions,
  selectedQuestion,
  password,
  userSecretQuestionList,
  userId,
  sN_upload,
  expires,
  expireDate,
  addInstructions,
  instructions,
  handleRTEChange,
  sendEmails,
  recipients,
  recipientListItems,
  emailMessage,
  receiveEmails,
  receivers,
  availableStaff,
  actions,
}) => {
  return (
    <>
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Who has access</strong>
          </p>
          <p>Control who can view this request link</p>
        </div>
        <div className="-inputs yt-col">
          {!isAuthTypeRestricted && (
            <SelectFromObject
              change={e => actions.setAuthType(e.target.value)}
              items={authTypes}
              display="display"
              displayStartCase={false}
              name="authType"
              selected={authType}
              value="val"
              disabled={isAuthTypeRestricted}
            />
          )}
          {authType === 'secret-question' && (
            <div>
              <SelectFromObject
                change={e =>
                  actions.setField('selectedQuestion', e.target.value)
                }
                items={secretQuestions}
                display="display"
                displayStartCase={false}
                name="selectedQuestion"
                selected={selectedQuestion}
                value="val"
              />
              {selectedQuestion === 'other' && (
                <TextInput
                  change={e =>
                    actions.updateSecretQuestion(
                      selectedQuestion,
                      'prompt',
                      e.target.value,
                    )
                  }
                  name={`secretQuestions.${selectedQuestion}.prompt`}
                  placeholder="Custom secret question"
                  required={true}
                  value={secretQuestions[selectedQuestion]?.prompt || ''}
                />
              )}
              <TextInput
                change={e => actions.setField('password', e.target.value)}
                helpText="Make sure the answer is something you both know"
                name="password"
                placeholder="Shared answer"
                required={true}
                value={password}
              />
            </div>
          )}
          {authType === 'password' && (
            <TextInput
              change={e => actions.setField('password', e.target.value)}
              helpText="It's up to you to make sure they know this password"
              name="password"
              placeholder="Set Password"
              required={true}
              value={password}
            />
          )}
          {authType === 'shared-contact-secret' && (
            <SelectFromObject
              change={e => actions.setField('userId', e.target.value)}
              items={userSecretQuestionList}
              display="display"
              displayStartCase={false}
              name="userId"
              placeholder="Select a contact"
              selected={userId}
              value="val"
            />
          )}
          <div className="alert-message warning -left -small">
            {authType === 'none' ? (
              <p>
                <small>
                  <strong>Important Note: </strong>Anyone with the link can
                  access this request.
                </small>
              </p>
            ) : authType === 'shared-client-secret' ? (
              <p>
                <small>
                  <strong>Important Note: </strong>Only those who know the
                  answer to the client's security question can access this
                  request.
                </small>
              </p>
            ) : authType === 'shared-contact-secret' ? (
              <p>
                <small>
                  <strong>Important Note: </strong>Only those who know the
                  answer to this contact's security question can access this
                  request.
                </small>
              </p>
            ) : (
              <p>
                <small>
                  <strong>Important Note: </strong>Only those who know the
                  answer to the question can access this request.
                </small>
              </p>
            )}
          </div>
        </div>
      </div>
      <hr />
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>Notify when uploaded</p>
        </div>
        <div className="-inputs yt-col">
          <ToggleSwitchInput
            change={e => actions.setField('sN_upload', e.target.value)}
            disabled={false}
            inputClasses="-right"
            name="sN_upload"
            required={false}
            rounded={true}
            value={sN_upload}
          />
        </div>
      </div>
      <hr />
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Expiration</strong>
          </p>
          <p>Disable this link on a specific date</p>
        </div>
        <div className="-inputs yt-col">
          <ToggleSwitchInput
            change={e => actions.setField('expires', e.target.value)}
            disabled={false}
            inputClasses="-right"
            name="expires"
            required={false}
            rounded={true}
            value={expires}
          />
          {expires && (
            <SingleDatePickerInput
              anchorDirection="right"
              change={e => actions.setField('expireDate', e.target.value)}
              enableOutsideDays={false}
              initialDate={expireDate}
              inputClasses="-right"
              minDate={DateTime.local().toMillis()}
              name="expireDate"
              numberOfMonths={1}
              placeholder=""
            />
          )}
        </div>
      </div>
      <hr />
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Add instructions</strong>
          </p>
          <p>Let the client know what you are requesting</p>
        </div>
        <div className="-inputs yt-col">
          <ToggleSwitchInput
            change={e => actions.setField('addInstructions', e.target.value)}
            disabled={false}
            inputClasses="-right"
            name="addInstructions"
            required={false}
            rounded={true}
            value={addInstructions}
          />
        </div>
        {addInstructions && (
          <div className="input-group">
            <ISReactDraftEditor
              onChange={handleRTEChange}
              defaultValue={instructions}
              title={null}
              placeholder="Please upload your W-2 for 2019"
            />
          </div>
        )}
      </div>
      <hr />
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Send emails</strong>
          </p>
          <p>Auto send emails when you create this link</p>
        </div>
        <div className="-inputs yt-col">
          <ToggleSwitchInput
            change={e => actions.toggleSendEmails(e.target.value)}
            disabled={false}
            inputClasses="-right"
            name="sendEmails"
            required={false}
            rounded={true}
            value={sendEmails}
          />
        </div>
      </div>
      {sendEmails && (
        <div className="yt-row space-between -share-and-request-recepient">
          <div
            className="yt-col"
            style={{ paddingLeft: '5px' }}
          >
            <button
              className="yt-btn xx-small u-pullRight"
              onClick={() => actions.addRecipient()}
            >
              <i className="fal fa-plus" /> Add another recipient
            </button>
            {recipients.map((recipient, i) =>
              recipient ? (
                <RecipientInput
                  change={e => {
                    const name = e.target.name
                    const value = e.target.value

                    if (name.includes('emailError')) {
                      if (recipient.emailError !== value) {
                        actions.updateRecipient(i, { emailError: value })
                      }
                    } else if (typeof value === 'string') {
                      const updates = { email: value }
                      actions.updateRecipient(i, updates)
                    } else if (typeof value === 'object' && value !== null) {
                      actions.updateRecipient(i, value)
                    }
                  }}
                  currentIndex={i}
                  key={'recipient_' + i}
                  recipientListItems={recipientListItems}
                  recipient={recipient}
                  removeRecipient={() => actions.removeRecipient(i)}
                  filterable={true}
                  hiddenBtn={false}
                />
              ) : null,
            )}
          </div>
          <div className="yt-row">
            <TextAreaInput
              change={e => actions.setField('emailMessage', e.target.value)}
              name="emailMessage"
              placeholder="Email message"
              value={emailMessage}
            />
          </div>
        </div>
      )}
      <hr />
      <div className="-setting yt-row space-between">
        <div className="-instructions yt-col">
          <p>
            <strong>Receive emails</strong>
          </p>
          <p>Auto receive emails when contact uploaded a file</p>
        </div>
        <div className="-inputs yt-col">
          <ToggleSwitchInput
            change={e => actions.toggleReceiveEmails(e.target.value)}
            disabled={false}
            inputClasses="-right"
            name="receiveEmails"
            required={false}
            rounded={true}
            value={receiveEmails}
          />
        </div>
      </div>
      {receiveEmails && (
        <div className="yt-row space-between -share-and-request-recepient">
          <div
            className="yt-col"
            style={{ paddingLeft: '5px' }}
          >
            <button
              className="yt-btn xx-small u-pullRight"
              onClick={() => actions.addReceiver()}
            >
              <i className="fal fa-plus" /> Add another recipient
            </button>
            {receivers.map((receiver, i) =>
              receiver ? (
                <RecipientInput
                  change={e => {
                    const value = e.target.value
                    if (receiver.email !== value) {
                      actions.updateReceiver(i, { email: value })
                    }
                  }}
                  currentIndex={i}
                  key={'receiver_' + i}
                  recipientListItems={availableStaff}
                  recipient={receiver}
                  removeRecipient={() => actions.removeReceiver(i)}
                  filterable={true}
                  hiddenBtn={true}
                />
              ) : null,
            )}
          </div>
        </div>
      )}
    </>
  )
}
